// src/components/Social/SocialWeeklySpotlight.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './SocialWeeklySpotlight.css';
import tzoneLogo from '../../assets/icons/logo.png';

import html2canvas from 'html2canvas';

// Leaflet (mapa interactivo en UI)
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Polyline, CircleMarker } from 'react-leaflet';

// ✅ Panel RRSS (1080x1920)
import ShareTrainingCardPanel from './SocialTrainingCard/ShareTrainingCardPanel';

// ✅ Iconos (ruta real: src/assets/icons/)
import iconRunning from '../../assets/icons/running.png';
import iconCycling from '../../assets/icons/cycling.png';
import iconSwim from '../../assets/icons/swim.png';
import iconStrength from '../../assets/icons/strength.png';
import iconTriathlon from '../../assets/icons/triathlon.png';
import iconWalk from '../../assets/icons/walk.png';
import iconOther from '../../assets/icons/other.png';
import iconDayoff from '../../assets/icons/dayoff.png';
import API_BASE_URL from '../../config/api';
import { useTranslation } from '../../i18n';

const API_URL = API_BASE_URL;
const HOST = (process.env.REACT_APP_HOST || API_URL).replace(/\/+$/, '');

const DEFAULT_ATHLETE_AVATAR = `${HOST}/uploads/athletes/default.jpg`;
const DEFAULT_TRAINER_AVATAR = `${HOST}/uploads/trainers/default.jpg`;

// ✅ Tiles CORS-friendly (para que el mapa en UI funcione bien)
const TILE_URL =
  process.env.REACT_APP_MAP_TILE_URL ||
  'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTR =
  process.env.REACT_APP_MAP_TILE_ATTR ||
  '&copy; OpenStreetMap contributors';
const TILE_SUBDOMAINS = process.env.REACT_APP_MAP_TILE_SUBDOMAINS || 'abc';

// ✅ logs
const DBG = false;
const log = (...args) => {
  if (DBG) console.log('[SWS]', ...args);
};

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

// ✅ espera 2 frames (React paint + layout)
function nextPaint() {
  return new Promise((r) => {
    if (typeof requestAnimationFrame !== 'function') return r();
    requestAnimationFrame(() => requestAnimationFrame(r));
  });
}

function safeId(v) {
  if (v == null) return '';
  const s = String(v).trim();
  if (!s || s === 'null' || s === 'undefined') return '';
  return s;
}

function getEffectiveUserId(userIdProp, viewerUserIdProp) {
  const fromUserId = safeId(userIdProp);
  if (fromUserId) return fromUserId;

  const fromViewer = safeId(viewerUserIdProp);
  if (fromViewer) return fromViewer;

  const ls =
    safeId(localStorage.getItem('userId')) ||
    safeId(localStorage.getItem('userID')) ||
    safeId(localStorage.getItem('userid'));

  return ls;
}

function buildHeadersJson(userId) {
  const token = safeId(localStorage.getItem('token'));
  const uid = safeId(userId);

  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (uid) headers['x-user-id'] = uid;
  return headers;
}

function toNum(v) {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const s = String(v).trim().replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
}

function formatDuration(min) {
  const totalSeconds = Math.round((Number(min) || 0) * 60);
  const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(totalSeconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function formatCompactHM(min) {
  const totalMinutes = Math.round(Number(min) || 0);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h <= 0) return `${m}m`;
  return `${h}:${String(m).padStart(2, '0')}h`;
}

function formatPaceFromSec(secPerKm) {
  if (!Number.isFinite(secPerKm) || secPerKm <= 0) return '--:--';
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * ✅ Duración robusta a minutos:
 * - "HH:MM:SS" -> min
 * - "MM:SS" -> min
 * - número grande típico de segundos -> seg -> min
 * - ms muy grandes -> ms -> min
 */
function parseDurationToMin(v) {
  if (v == null) return 0;

  if (typeof v === 'number' && Number.isFinite(v)) {
    const n = v;
    if (n >= 200000) return Math.round(n / 60000); // ms -> min
    if (n > 1000) return Math.round(n / 60); // seg -> min
    return n; // min
  }

  const s0 = String(v).trim();
  if (!s0) return 0;
  const s = s0.toLowerCase();

  // "hh:mm:ss"
  let m = s.match(/^(\d{1,3}):(\d{2}):(\d{2})$/);
  if (m) {
    const hh = Number(m[1]) || 0;
    const mm = Number(m[2]) || 0;
    const ss = Number(m[3]) || 0;
    return hh * 60 + mm + Math.round(ss / 60);
  }

  // "mm:ss"
  m = s.match(/^(\d{1,4}):(\d{2})$/);
  if (m) {
    const mm = Number(m[1]) || 0;
    const ss = Number(m[2]) || 0;
    return mm + Math.round(ss / 60);
  }

  // fallback numérico
  const n = Number(s0);
  if (!Number.isFinite(n)) return 0;
  if (n >= 200000) return Math.round(n / 60000);
  if (n > 1000) return Math.round(n / 60);
  return n;
}

function normalizeDistanceKm(val) {
  if (val == null) return 0;

  if (typeof val === 'string') {
    const s = val.trim().toLowerCase();
    const num = parseFloat(s.replace(',', '.'));
    if (!Number.isFinite(num)) return 0;

    if (s.includes(' km')) return num;
    if (s.includes(' m')) return num / 1000;

    return num > 2000 ? num / 1000 : num;
  }

  const n = Number(val);
  if (!Number.isFinite(n)) return 0;
  return n > 2000 ? n / 1000 : n;
}

/** ✅ Clave de día robusta */
function toDayKeySmart(d) {
  if (!d) return '';

  if (typeof d === 'string') {
    // 1) si es date-only, úsalo directo
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;

    // 2) si trae YYYY-MM-DD al inicio
    const m = d.match(/^(\d{4}-\d{2}-\d{2})/);

    // ✅ clave: si es ISO UTC (termina en Z), usa UTC getters (evita corrimiento a sábado)
    if (/Z$/i.test(d)) {
      const dt = new Date(d);
      if (!Number.isNaN(dt.getTime())) {
        const y = dt.getUTCFullYear();
        const mo = String(dt.getUTCMonth() + 1).padStart(2, '0');
        const da = String(dt.getUTCDate()).padStart(2, '0');
        return `${y}-${mo}-${da}`;
      }
    }

    // si no es Z, y había match, úsalo tal cual
    if (m) return m[1];

    // fallback: parse local
    const dt = new Date(d);
    if (!Number.isNaN(dt.getTime())) {
      const y = dt.getFullYear();
      const mo = String(dt.getMonth() + 1).padStart(2, '0');
      const da = String(dt.getDate()).padStart(2, '0');
      return `${y}-${mo}-${da}`;
    }
    return '';
  }

  // Date u otro
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const mo = String(dt.getMonth() + 1).padStart(2, '0');
  const da = String(dt.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

// ✅ Semana Lun→Dom
function startOfWeekMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Dom,1=Lun...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}
function endOfWeekSunday(date) {
  const s = startOfWeekMonday(date);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function cap(s) {
  const t = String(s || '').trim();
  if (!t) return '';
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function shortDow(date) {
  try {
    const raw = new Intl.DateTimeFormat('es-CL', { weekday: 'short' }).format(date);
    return raw.replace('.', '');
  } catch {
    return '';
  }
}
function shortDM(date) {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}`;
}

/** ✅ Detecta deporte desde múltiples fuentes */
function detectSportKeyFromActivity(a) {
  const raw =
    a?.type ||
    a?.sport ||
    a?.activityType ||
    a?.discipline ||
    a?.category ||
    a?.planned?.sport ||
    a?.planned?.type ||
    a?.completed?.sport ||
    '';

  const t = String(raw || '').toLowerCase();

  if (t.includes('run') || t.includes('correr') || t.includes('running') || t.includes('trote') || t.includes('trail'))
    return 'running';

  if (t.includes('bike') || t.includes('cycling') || t.includes('cicl') || t.includes('mtb') || t.includes('rodillo'))
    return 'cycling';

  if (t.includes('swim') || t.includes('swimming') || t.includes('nadar') || t.includes('natacion') || t.includes('natación'))
    return 'swim';

  if (t.includes('strength') || t.includes('fuerza') || t.includes('gym') || t.includes('pesas') || t.includes('fit'))
    return 'strength';

  if (t.includes('tri') || t.includes('triat')) return 'triathlon';

  if (t.includes('walk') || t.includes('caminar') || t.includes('hike') || t.includes('trek')) return 'walk';

  return 'other';
}

function sportIconForKey(key) {
  switch (key) {
    case 'running':
      return iconRunning;
    case 'cycling':
      return iconCycling;
    case 'swim':
      return iconSwim;
    case 'strength':
      return iconStrength;
    case 'triathlon':
      return iconTriathlon;
    case 'walk':
      return iconWalk;
    case 'dayoff':
      return iconDayoff;
    default:
      return iconOther;
  }
}

function getActId(a) {
  return safeId(a?._id || a?.id || a?.activityId || a?.uuid);
}

/**
 * ✅ IMPORTANTÍSIMO:
 * Desde ahora NO usamos planificado para estas métricas.
 * Si no hay completed/persistido → devolvemos 0 y la UI cae a parsedMeta.
 */
function getActDistanceKm(a) {
  const completed = a?.completed || {};
  const dist =
    completed?.distance ??
    completed?.distancia ??
    completed?.total_distance ??
    completed?.totalDistance ??
    a?.distance ??
    a?.distancia ??
    a?.total_distance ??
    a?.totalDistance ??
    a?.session?.total_distance ??
    a?.session?.totalDistance ??
    0;

  return normalizeDistanceKm(dist);
}

function getActDurationMin(a) {
  const completed = a?.completed || {};
  const dur =
    completed?.duracion ??
    completed?.duration ??
    completed?.total_timer_time ??
    completed?.totalTimerTime ??
    completed?.total_elapsed_time ??
    completed?.totalElapsedTime ??
    a?.duracion ??
    a?.duration ??
    a?.total_timer_time ??
    a?.totalTimerTime ??
    a?.total_elapsed_time ??
    a?.totalElapsedTime ??
    a?.activity?.total_timer_time ??
    a?.activity?.totalTimerTime ??
    a?.session?.total_timer_time ??
    a?.session?.totalTimerTime ??
    a?.session?.total_elapsed_time ??
    a?.session?.totalElapsedTime ??
    0;

  return parseDurationToMin(dur);
}

function getActTss(a) {
  const completed = a?.completed || {};
  const metrics = a?.metrics || a?.completed?.metrics || {};

  const tssRaw =
    completed?.tss ??
    completed?.hrTss ??
    completed?.tss_hr ??
    metrics?.tss ??
    metrics?.TSS ??
    a?.tss ??
    a?.hrTss ??
    a?.tss_hr ??
    0;

  const n = Number(tssRaw);
  return Number.isFinite(n) ? n : 0;
}

function getActElevGain(a) {
  const c = a?.completed || {};
  const v =
    c?.elevGain ??
    c?.elevationGain ??
    c?.totalAscent ??
    c?.ascent ??
    0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function getActElevLoss(a) {
  const c = a?.completed || {};
  const v =
    c?.elevLoss ??
    c?.elevationLoss ??
    c?.totalDescent ??
    c?.descent ??
    0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ✅ elige actividad “principal” del día
function pickPrimaryAct(acts) {
  const list = Array.isArray(acts) ? acts : [];
  if (!list.length) return null;

  const priority = { running: 5, cycling: 4, swim: 3, strength: 2, triathlon: 2, walk: 1, other: 0 };

  const scored = list
    .map((a) => {
      const k = detectSportKeyFromActivity(a);
      const dur = getActDurationMin(a);
      return { a, score: (priority[k] || 0) * 100000 + dur * 10 };
    })
    .sort((x, y) => y.score - x.score);

  return scored[0]?.a || list[0];
}

function normalizeRoutesByDay(ws) {
  const raw = ws?.routesByDay;
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const k of Object.keys(raw)) {
    const nk = toDayKeySmart(k);
    out[nk || k] = raw[k];
  }
  return out;
}

async function fetchJson(url, opts) {
  const res = await fetch(url, opts);
  const data = await res.json().catch(() => null);
  return { ok: res.ok, status: res.status, data };
}

/* =========================
   ✅ Coordenadas robustas
   ========================= */
const SEMI_TO_DEG = 180 / 2147483648; // 2^31
function toDegMaybe(v) {
  const n = toNum(v);
  if (!Number.isFinite(n)) return NaN;
  if (Math.abs(n) <= 180) return n; // ya es grado
  if (Math.abs(n) <= 2147483648) return n * SEMI_TO_DEG; // semicircle
  return n;
}

function isValidLatLon(lat, lon) {
  return Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

function coerceLatLonPair(a, b) {
  let x = toDegMaybe(a);
  let y = toDegMaybe(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  // si viene [lon,lat] => swap
  const xLooksLat = Math.abs(x) <= 90;
  const yLooksLat = Math.abs(y) <= 90;

  if (!xLooksLat && yLooksLat) {
    const lat = y;
    const lon = x;
    return isValidLatLon(lat, lon) ? [lat, lon] : null;
  }
  return isValidLatLon(x, y) ? [x, y] : null;
}

function computeBoundsSafe(points) {
  if (!Array.isArray(points) || points.length < 2) return null;

  let minLat = 999, maxLat = -999, minLon = 999, maxLon = -999;

  for (const pt of points) {
    const lat = Array.isArray(pt) ? toNum(pt[0]) : NaN;
    const lon = Array.isArray(pt) ? toNum(pt[1]) : NaN;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;

    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
  }

  if (minLat === 999) return null;
  return [[minLat, minLon], [maxLat, maxLon]];
}

/** ✅ busca fileId “real” para llamar /api/fit/parsed-data/:id */
function getFileIdFromActivity(a) {
  const candidates = [
    a?.fileId,
    a?.fitFileId,
    a?.parsedDataId,
    a?.processedDataId,
    a?.processedId,

    a?.completed?.fileId,
    a?.completed?.fitFileId,
    a?.completed?.processedDataId,
    a?.completed?.parsedDataId,

    a?.fit?.fileId,
    a?.fitFile?.fileId,
    a?.fitFileDetails?.fileId,

    a?.processedData?.fileId,
    a?.processedData?._id,
    a?.processedData?.id,

    a?.file?._id,
    a?.file?.id,
    a?.file?.fileId,
  ];

  for (const c of candidates) {
    const id = safeId(c);
    if (id) return id;
  }
  return '';
}

function downsample(points, maxPts = 2500) {
  if (!Array.isArray(points)) return [];
  if (points.length <= maxPts) return points;
  const step = Math.ceil(points.length / maxPts);
  const out = [];
  for (let i = 0; i < points.length; i += step) out.push(points[i]);
  if (out.length && out[out.length - 1] !== points[points.length - 1]) out.push(points[points.length - 1]);
  return out;
}

/* =========================
   ✅ SVG “mapa” para compartir (sin CORS)
   ========================= */
function RouteSvgPreview({ points }) {
  const W = 900;
  const H = 420;
  const pad = 24;

  const path = useMemo(() => {
    const pts = Array.isArray(points) ? points : [];
    if (pts.length < 2) return { poly: '', start: null, end: null };

    let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
    for (const [lat, lon] of pts) {
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
    }
    if (!Number.isFinite(minLat)) return { poly: '', start: null, end: null };

    const dx = Math.max(1e-9, maxLon - minLon);
    const dy = Math.max(1e-9, maxLat - minLat);

    const scaleX = (W - pad * 2) / dx;
    const scaleY = (H - pad * 2) / dy;
    const s = Math.min(scaleX, scaleY);

    const ox = pad + (W - pad * 2 - dx * s) / 2;
    const oy = pad + (H - pad * 2 - dy * s) / 2;

    const toXY = ([la, lo]) => {
      const x = ox + (lo - minLon) * s;
      const y = oy + (maxLat - la) * s; // invert Y
      return [x, y];
    };

    const xy = pts.map(toXY);
    const poly = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

    const start = xy[0];
    const end = xy[xy.length - 1];

    return { poly, start, end };
  }, [points]);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" role="img" aria-label="Ruta">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f8fafc" />
          <stop offset="1" stopColor="#eef2ff" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width={W} height={H} rx="22" fill="url(#bg)" />
      <rect x="14" y="14" width={W - 28} height={H - 28} rx="16" fill="#ffffff" opacity="0.75" />

      {path.poly ? (
        <>
          <polyline
            points={path.poly}
            fill="none"
            stroke="#2563eb"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.95"
          />
          {path.start && <circle cx={path.start[0]} cy={path.start[1]} r="10" fill="#16a34a" />}
          {path.end && <circle cx={path.end[0]} cy={path.end[1]} r="10" fill="#dc2626" />}
        </>
      ) : (
        <text x={W / 2} y={H / 2} textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize="22">
          Ruta no disponible
        </text>
      )}
    </svg>
  );
}

/* =========================
   ✅ Esperar recursos (evita capturas “a medias”)
   ========================= */
async function waitForImages(rootEl, timeoutMs = 6000) {
  if (!rootEl) return;
  const imgs = Array.from(rootEl.querySelectorAll('img'));
  if (!imgs.length) return;

  const start = Date.now();
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });

        const t = setInterval(() => {
          if (img.complete) {
            clearInterval(t);
            resolve();
          }
          if (Date.now() - start > timeoutMs) {
            clearInterval(t);
            resolve();
          }
        }, 150);
      });
    })
  );
}

/* =========================
   ✅ Fotos contactos
   ========================= */
function resolvePhotoUrl(raw, { scope } = {}) {
  if (!raw) return '';
  const v = String(raw).trim();
  if (!v || v.toLowerCase() === 'undefined' || v.toLowerCase() === 'null') return '';

  if (/^(https?:)?\/\//i.test(v) || v.startsWith('data:') || v.startsWith('blob:')) return v;
  if (v.startsWith('/')) return `${HOST}${v}`;
  if (/^uploads\//i.test(v)) return `${HOST}/${v}`;

  if (scope === 'trainer') return `${HOST}/uploads/trainers/${v}`;
  if (scope === 'athlete') return `${HOST}/uploads/athletes/${v}`;
  return `${HOST}/${v}`;
}

/* =========================
   ✅ CONTACTOS (robusto)
   ========================= */
function normalizeContactsPayload(data) {
  if (Array.isArray(data)) return data;

  const candidates = [
    data?.contacts,
    data?.items,
    data?.results,
    data?.data,
    data?.matches,
    data?.rows,
    data?.list,
    data?.friends,
  ];

  for (const c of candidates) if (Array.isArray(c)) return c;

  const deep = data?.data;
  if (deep && typeof deep === 'object') {
    const deepCandidates = [deep?.contacts, deep?.items, deep?.results, deep?.matches];
    for (const c of deepCandidates) if (Array.isArray(c)) return c;
  }

  return [];
}

function normalizeContactItem(x) {
  if (!x) return null;

  const u =
    x.user ||
    x.contact ||
    x.profile ||
    x.athlete ||
    x.partner ||
    x.match ||
    x.otherUser ||
    x.contactUser ||
    x.member ||
    x.toUser ||
    x.fromUser ||
    null;

  const src = u || x;

  const id = safeId(
    src?.userId ||
      src?._id ||
      src?.id ||
      x?.userId ||
      x?._id ||
      x?.id ||
      x?.athleteId ||
      x?.contactId
  );
  if (!id) return null;

  const name = String(
    src?.name ||
      src?.fullName ||
      src?.displayName ||
      [src?.firstName, src?.lastName].filter(Boolean).join(' ') ||
      src?.username ||
      src?.email ||
      x?.name ||
      x?.fullName ||
      x?.displayName ||
      'Contacto'
  ).trim();

  const avatar = safeId(
    src?.avatar ||
      src?.photo ||
      src?.picture ||
      src?.profilePic ||
      src?.imageUrl ||
      src?.img ||
      x?.avatar ||
      x?.photo ||
      x?.picture
  );

  const roleRaw = String(
    src?.role ||
      src?.userRole ||
      src?.type ||
      x?.role ||
      x?.userRole ||
      x?.type ||
      x?.scope ||
      ''
  )
    .trim()
    .toLowerCase();

  let role = roleRaw;
  if (role === 'coach' || role === 'entrenador') role = 'trainer';
  if (role === 'deportista') role = 'athlete';
  if (role && !['trainer', 'athlete'].includes(role)) role = '';

  return { id, name, avatar, role, raw: x };
}

async function fetchMatchContactsSmart(uid) {
  const headers = buildHeadersJson(uid);

  const endpoints = [
    `${API_URL}/api/social/contacts/matches`,
    `${API_URL}/api/social/matches`,
    `${API_URL}/api/social/matches?userId=${encodeURIComponent(uid)}`,
    `${API_URL}/api/social/contacts/matches?userId=${encodeURIComponent(uid)}`,
    `${API_URL}/api/social/contacts?type=match`,
    `${API_URL}/api/social/contacts?onlyMatches=true`,
    `${API_URL}/api/social/contacts?matches=true`,
    `${API_URL}/api/social/contacts`,
    `${API_URL}/api/social/contacts?userId=${encodeURIComponent(uid)}`,
    `${API_URL}/api/social/my-contacts`,
    `${API_URL}/api/social/my-contacts?userId=${encodeURIComponent(uid)}`,
    `${API_URL}/api/social/contacts/list`,
    `${API_URL}/api/social/contacts/list?userId=${encodeURIComponent(uid)}`,
    `${API_URL}/api/social/contacts/${encodeURIComponent(uid)}`,
    `${API_URL}/api/social/matches/${encodeURIComponent(uid)}`,
  ];

  let lastStatus = 0;

  for (const url of endpoints) {
    try {
      const r = await fetchJson(url, { headers });
      lastStatus = r.status;
      if (!r.ok) continue;

      const raw = normalizeContactsPayload(r.data);

      const seen = new Set();
      const list = raw
        .map(normalizeContactItem)
        .filter(Boolean)
        .filter((c) => {
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        });

      if (list.length) return list;
    } catch (_) {}
  }

  if (lastStatus === 401 || lastStatus === 403) {
    throw new Error('No autorizado al cargar contactos (401/403). Revisa token o el endpoint de Mis contactos.');
  }

  return [];
}

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    try {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result || ''));
      fr.onerror = () => reject(new Error('FileReader error'));
      fr.readAsDataURL(blob);
    } catch (e) {
      reject(e);
    }
  });
}

async function trySendShareToBackend({ uid, toUserId, text, blob, fileName, meta }) {
  if (!uid || !toUserId || !blob) return { ok: false };

  let dataUrl = '';
  try {
    dataUrl = await blobToDataUrl(blob);
  } catch (_) {
    dataUrl = '';
  }

  const body = {
    toUserId: String(toUserId),
    text: String(text || ''),
    type: 'weekly_share',
    fileName,
    imageDataUrl: dataUrl || null,
    meta: meta || {},
  };

  const headers = buildHeadersJson(uid);

  const endpoints = [
    `${API_URL}/api/social/messages/share-json`,
    `${API_URL}/api/social/messages/send-json`,
    `${API_URL}/api/social/messages/share`,
    `${API_URL}/api/social/messages/send`,
    `${API_URL}/api/social/messages`,
  ];

  for (const url of endpoints) {
    try {
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) {
        let data = null;
        try {
          data = await res.json();
        } catch (_) {
          data = null;
        }
        return { ok: true, mode: 'json', url, data };
      }
    } catch (_) {}
  }

  return { ok: false };
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function SocialWeeklySpotlight({
  userId,
  viewerUserId,
  days = 7,
  onGoDashboard,
  title = null,
  fullBleed = true,
}) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const mapRef = useRef(null);

  // modal share
  const [shareOpen, setShareOpen] = useState(false);
  const sharePreviewRef = useRef(null);
  const [shareContacts, setShareContacts] = useState([]);
  const [shareContactsLoading, setShareContactsLoading] = useState(false);
  const [shareContactsErr, setShareContactsErr] = useState('');
  const [shareToId, setShareToId] = useState('');
  const [shareNote, setShareNote] = useState('');

  // RRSS modal
  const [rrssOpen, setRrssOpen] = useState(false);

  // estado general
  const [sharing, setSharing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ws, setWs] = useState(null);
  const [err, setErr] = useState('');
  const [weekActs, setWeekActs] = useState([]);
  const [selectedDayKey, setSelectedDayKey] = useState('');
  const [weekCursor, setWeekCursor] = useState(() => startOfWeekMonday(new Date()));

  // ✅ selector de sesión dentro del día
  const [selectedActId, setSelectedActId] = useState('');

  // parsed-data
  const [routeFromParsed, setRouteFromParsed] = useState([]); // [[lat,lon],]
  const [elevFromParsed, setElevFromParsed] = useState([]); // [alt,]
  const [parsedLoading, setParsedLoading] = useState(false);
  const [parsedMeta, setParsedMeta] = useState({ durationMin: 0, distanceKm: 0, tss: 0 });

  const parsedCacheRef = useRef(new Map()); // fileId -> { points, elev, meta }

  // ✅ reemplazo seguro de cacheTick (sin warnings)
  const cacheTickRef = useRef(0);
  const [cacheRenderTick, setCacheRenderTick] = useState(0);
  const bumpCacheTick = useCallback(() => {
    cacheTickRef.current += 1;
    setCacheRenderTick((t) => t + 1); // fuerza re-render para que memos se recalculen
  }, []);

  const uid = useMemo(() => getEffectiveUserId(userId, viewerUserId), [userId, viewerUserId]);

  const weekStart = useMemo(() => startOfWeekMonday(weekCursor), [weekCursor]);
  const weekEnd = useMemo(() => endOfWeekSunday(weekCursor), [weekCursor]);
  const weekStartKey = useMemo(() => toDayKeySmart(weekStart), [weekStart]);
  const weekEndKey = useMemo(() => toDayKeySmart(weekEnd), [weekEnd]);

  const weekLabel = useMemo(
    () =>
      t('social.weeklySpotlight.weekRange', {
        start: weekStartKey,
        end: weekEndKey,
      }),
    [weekStartKey, weekEndKey, t]
  );

  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setErr('');
        setWs(null);
        setWeekActs([]);

        if (!uid) {
          setErr('Falta userId (x-user-id) para cargar el resumen semanal.');
          return;
        }

        const headers = buildHeadersJson(uid);

        const wsUrl =
          `${API_URL}/api/fit/weekly-summary?week=true&includeRoute=true` +
          `&days=${encodeURIComponent(days)}` +
          `&start=${encodeURIComponent(weekStartKey)}&end=${encodeURIComponent(weekEndKey)}`;

        const wsResp = await fetchJson(wsUrl, { headers, signal: ctrl.signal });
        if (wsResp.ok) setWs(wsResp.data);
        else setWs(null);

        const endExclusiveKey = toDayKeySmart(addDays(weekEnd, 1));

        const actsUrl = `${API_URL}/api/activities/athlete/${encodeURIComponent(uid)}?start=${encodeURIComponent(
          weekStartKey
        )}&end=${encodeURIComponent(endExclusiveKey)}`;

        const actsResp = await fetchJson(actsUrl, { headers, signal: ctrl.signal });

        console.log('[SWS][range]', { weekStartKey, weekEndKey, actsUrl });
        console.log('[SWS][actsResp]', {
          ok: actsResp.ok,
          status: actsResp.status,
          count: Array.isArray(actsResp.data) ? actsResp.data.length : null,
          sample: Array.isArray(actsResp.data) ? actsResp.data.slice(0, 3) : actsResp.data,
        });

        if (!mounted) return;

        if (actsResp.ok && Array.isArray(actsResp.data)) setWeekActs(actsResp.data);
        else setWeekActs([]);
      } catch (e) {
        if (!mounted) return;
        if (e?.name === 'AbortError') return;
        setErr(e?.message || 'Error cargando resumen semanal');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [uid, days, weekStartKey, weekEndKey, weekEnd]);

  const actsByDay = useMemo(() => {
    const map = new Map();
    for (const a of weekActs || []) {
      const key = toDayKeySmart(
        a?.completedAt ||
          a?.completed?.completedAt ||
          a?.date ||
          a?.start ||
          a?.createdAt ||
          a?.when
      );

      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(a);
    }
    for (const [k, list] of map.entries()) {
      list.sort((x, y) => {
        const ax = new Date(
          x?.completedAt ||
            x?.completed?.completedAt ||
            x?.date ||
            x?.start ||
            x?.createdAt ||
            0
        ).getTime();

        const ay = new Date(y?.date || y?.start || y?.createdAt || 0).getTime();
        return ax - ay;
      });
      map.set(k, list);
    }
    return map;
  }, [weekActs]);

  const totals = useMemo(() => {
    // ✅ usa cacheRenderTick para recalcular cuando cambia el cache (sin warnings)
    void cacheRenderTick;

    let sessions = 0;
    let distanceKm = 0;
    let durationMin = 0;
    let tss = 0;
    let elevGain = 0;
    let elevLoss = 0;

    for (const a of weekActs || []) {
      sessions += 1;

      const fid = getFileIdFromActivity(a);
      const cached = fid ? parsedCacheRef.current.get(fid) : null;

      distanceKm += (cached?.meta?.distanceKm > 0) ? cached.meta.distanceKm : getActDistanceKm(a);
      durationMin += (cached?.meta?.durationMin > 0) ? cached.meta.durationMin : getActDurationMin(a);
      tss += (cached?.meta?.tss > 0) ? cached.meta.tss : getActTss(a);

      elevGain += getActElevGain(a);
      elevLoss += getActElevLoss(a);
    }

    const wsTotals = ws?.totals || {};
    if ((weekActs || []).length === 0 && wsTotals) {
      return {
        sessions: Number(wsTotals.sessions || 0) || 0,
        distanceKm: Number(wsTotals.distanceKm || 0) || 0,
        durationMin: Number(wsTotals.durationMin || 0) || 0,
        tss: Number(wsTotals.tss || 0) || 0,
        elevGain: Number(wsTotals.elevGain || wsTotals.elevationGain || 0) || 0,
        elevLoss: Number(wsTotals.elevLoss || wsTotals.elevationLoss || 0) || 0,
      };
    }

    return { sessions, distanceKm, durationMin, tss, elevGain, elevLoss };
  }, [weekActs, ws, cacheRenderTick]);

  // ✅ rutas del weekly-summary
  const routeByDay = useMemo(() => normalizeRoutesByDay(ws), [ws]);

  // ✅ ws.days por fecha (YYYY-MM-DD) -> stats del backend weekly-summary
  const wsDaysByDate = useMemo(() => {
    const m = new Map();
    const arr = Array.isArray(ws?.days) ? ws.days : [];
    for (const d of arr) {
      const key = safeId(d?.date); // viene "YYYY-MM-DD"
      if (key) m.set(key, d);
    }
    return m;
  }, [ws]);

  const dayTiles = useMemo(() => {
    void cacheRenderTick;

    const tiles = [];

    for (let i = 0; i < 7; i++) {
      const d = addDays(weekStart, i);
      const key = toDayKeySmart(d);

      const acts = actsByDay.get(key) || [];

      const sessionsFromActs = acts.length;
      let distanceKmFromActs = 0;
      let durationMinFromActs = 0;
      let tssFromActs = 0;

      for (const a of acts) {
        const fid = getFileIdFromActivity(a);
        const cached = fid ? parsedCacheRef.current.get(fid) : null;

        const dk = (cached?.meta?.distanceKm > 0) ? cached.meta.distanceKm : getActDistanceKm(a);
        const dm = (cached?.meta?.durationMin > 0) ? cached.meta.durationMin : getActDurationMin(a);
        const ts = (cached?.meta?.tss > 0) ? cached.meta.tss : getActTss(a);

        distanceKmFromActs += dk;
        durationMinFromActs += dm;
        tssFromActs += ts;
      }

      const wsDay = wsDaysByDate.get(key) || null;
      const sessionsFromWs = Number(wsDay?.sessions || 0) || 0;
      const distanceKmFromWs = Number(wsDay?.distanceKm || 0) || 0;
      const durationMinFromWs = Number(wsDay?.durationMin || 0) || 0;

      const sessions = sessionsFromActs > 0 ? sessionsFromActs : sessionsFromWs;
      const distanceKm = sessionsFromActs > 0 ? distanceKmFromActs : distanceKmFromWs;
      const durationMin = sessionsFromActs > 0 ? durationMinFromActs : durationMinFromWs;

      const tss = sessionsFromActs > 0 ? tssFromActs : 0;

      const primaryAct = pickPrimaryAct(acts);

      const primarySport =
        primaryAct ? detectSportKeyFromActivity(primaryAct)
        : sessions > 0 ? 'other'
        : 'dayoff';

      const titleText =
        primaryAct?.title ||
        primaryAct?.name ||
        primaryAct?.workoutName ||
        primaryAct?.planned?.title ||
        primaryAct?.planned?.name ||
        '';

      tiles.push({
        key,
        date: d,
        sessions,
        distanceKm,
        durationMin,
        tss,
        activities: acts,
        primaryAct,
        primarySport,
        titleText: String(titleText || '').trim(),
      });
    }

    return tiles;
  }, [weekStart, actsByDay, wsDaysByDate, cacheRenderTick]);

  useEffect(() => {
    if (!dayTiles.length) return;

    const todayKey = toDayKeySmart(new Date());
    const inWeek = dayTiles.some((t) => t.key === todayKey);

    setSelectedDayKey((prev) => {
      if (prev && dayTiles.some((t) => t.key === prev)) return prev;
      return inWeek ? todayKey : dayTiles[0]?.key || '';
    });
  }, [weekStartKey, dayTiles]);

  useEffect(() => {
    setSelectedActId('');
  }, [selectedDayKey]);

  const realSelectedTile = useMemo(
    () => dayTiles.find((t) => t.key === selectedDayKey) || null,
    [dayTiles, selectedDayKey]
  );

  const selectedActs = useMemo(() => realSelectedTile?.activities || [], [realSelectedTile]);

  const selectedPrimary = useMemo(() => {
    if (!selectedActs.length) return null;

    const wanted = safeId(selectedActId);
    if (wanted) {
      const found = selectedActs.find((a, idx) => {
        const key = safeId(getActId(a) || getFileIdFromActivity(a) || `idx-${idx}`);
        return key === wanted;
      });
      if (found) return found;
    }

    return pickPrimaryAct(selectedActs);
  }, [selectedActs, selectedActId]);

  // ✅ Prefetch META de todas las sesiones del día
  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        if (!uid) return;
        const acts = Array.isArray(selectedActs) ? selectedActs : [];
        if (!acts.length) return;

        const headers = buildHeadersJson(uid);

        for (const a of acts) {
          const fid = getFileIdFromActivity(a);
          if (!fid) continue;

          const cached = parsedCacheRef.current.get(fid);
          if (cached?.meta) continue;

          const url = `${API_URL}/api/fit/parsed-data/${encodeURIComponent(fid)}`;
          const resp = await fetchJson(url, { headers, signal: ctrl.signal });
          if (!mounted) return;
          if (!resp.ok || !resp.data) continue;

          const parsed = resp.data;

          const baseSession = parsed?.session || {};
          const extSession = Array.isArray(parsed?.sessions) ? (parsed.sessions[0] || {}) : (parsed?.sessions || {});
          const session = { ...baseSession, ...extSession };
          const activity = parsed?.activity || {};
          const metrics = parsed?.metrics || {};
          const completed = parsed?.completed || {};

          const durSec = toNum(
            session?.total_timer_time ??
              session?.totalTimerTime ??
              session?.total_elapsed_time ??
              session?.totalElapsedTime ??
              activity?.total_timer_time ??
              activity?.totalTimerTime ??
              completed?.duracion ??
              completed?.duration ??
              0
          );

          const distM = toNum(
            session?.total_distance ??
              session?.totalDistance ??
              activity?.total_distance ??
              activity?.totalDistance ??
              completed?.distance ??
              completed?.distancia ??
              0
          );

          const tssVal = toNum(
            parsed?.tss ??
              metrics?.tss ??
              metrics?.TSS ??
              completed?.tss ??
              completed?.hrTss ??
              0
          );

          const meta = {
            durationMin: durSec ? durSec / 60 : 0,
            distanceKm: distM ? distM / 1000 : 0,
            tss: tssVal || 0,
          };

          parsedCacheRef.current.set(fid, { ...(cached || {}), meta });
        }
      } catch (e) {
        if (e?.name === 'AbortError') return;
        log('prefetch meta error', e);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [uid, selectedActs]);

  const selectedSportKey = useMemo(
    () => (selectedPrimary ? detectSportKeyFromActivity(selectedPrimary) : 'dayoff'),
    [selectedPrimary]
  );

  const selectedLabel = useMemo(() => {
    if (!realSelectedTile) return '';
    return `${cap(shortDow(realSelectedTile.date))} ${shortDM(realSelectedTile.date)}`;
  }, [realSelectedTile]);

  const pointsFromWeeklySummary = useMemo(() => {
    const r = selectedDayKey ? routeByDay[selectedDayKey] : null;
    const raw = r?.points;
    if (!Array.isArray(raw) || raw.length < 2) return [];

    const out = [];
    for (const p of raw) {
      if (Array.isArray(p) && p.length >= 2) {
        const pt = coerceLatLonPair(p[0], p[1]);
        if (pt) out.push(pt);
      } else if (p && typeof p === 'object') {
        const pt = coerceLatLonPair(p.lat ?? p.latitude, p.lon ?? p.lng ?? p.longitude);
        if (pt) out.push(pt);
      }
    }
    return out.length > 1 ? out : [];
  }, [routeByDay, selectedDayKey]);

  const selectedFileId = useMemo(() => {
    const fromAct = getFileIdFromActivity(selectedPrimary);
    if (fromAct) return fromAct;

    const r = selectedDayKey ? routeByDay[selectedDayKey] : null;
    const fromWs = safeId(r?.fileId || r?.fitFileId || r?.processedDataId || r?.parsedDataId);
    if (fromWs) return fromWs;

    return '';
  }, [selectedPrimary, selectedDayKey, routeByDay]);

  // ✅ FETCH parsed-data para ruta REAL (y meta del seleccionado)
  useEffect(() => {
    let mounted = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        setParsedLoading(true);

        if (!uid || !selectedFileId) {
          setRouteFromParsed([]);
          setElevFromParsed([]);
          setParsedMeta({ durationMin: 0, distanceKm: 0, tss: 0 });
          return;
        }

        const cached = parsedCacheRef.current.get(selectedFileId);
        if (cached?.points?.length > 1) {
          setRouteFromParsed(cached.points);
          setElevFromParsed(cached.elev || []);
          if (cached.meta) setParsedMeta(cached.meta);
        } else {
          setRouteFromParsed([]);
          setElevFromParsed([]);
          setParsedMeta(cached?.meta || { durationMin: 0, distanceKm: 0, tss: 0 });
        }

        const headers = buildHeadersJson(uid);
        const url = `${API_URL}/api/fit/parsed-data/${encodeURIComponent(selectedFileId)}`;

        const resp = await fetchJson(url, { headers, signal: ctrl.signal });
        if (!mounted) return;
        if (!resp.ok || !resp.data) return;

        const parsed = resp.data;
        const records = Array.isArray(parsed?.records) ? parsed.records : [];

        const baseSession = parsed?.session || {};
        const extSession = Array.isArray(parsed?.sessions) ? (parsed.sessions[0] || {}) : (parsed?.sessions || {});
        const session = { ...baseSession, ...extSession };
        const activity = parsed?.activity || {};
        const metrics = parsed?.metrics || {};
        const completed = parsed?.completed || {};

        const durSec = toNum(
          session?.total_timer_time ??
            session?.totalTimerTime ??
            session?.total_elapsed_time ??
            session?.totalElapsedTime ??
            activity?.total_timer_time ??
            activity?.totalTimerTime ??
            completed?.duracion ??
            completed?.duration ??
            0
        );

        const distM = toNum(
          session?.total_distance ??
            session?.totalDistance ??
            activity?.total_distance ??
            activity?.totalDistance ??
            completed?.distance ??
            completed?.distancia ??
            0
        );

        const tssVal = toNum(
          parsed?.tss ??
            metrics?.tss ??
            metrics?.TSS ??
            completed?.tss ??
            completed?.hrTss ??
            0
        );

        const meta = {
          durationMin: durSec ? (durSec / 60) : 0,
          distanceKm: distM ? (distM / 1000) : 0,
          tss: tssVal || 0,
        };
        setParsedMeta(meta);

        const coords = [];
        for (const r of records) {
          const latRaw = r?.position_lat ?? r?.lat ?? r?.latitude;
          const lonRaw = r?.position_long ?? r?.position_lon ?? r?.lon ?? r?.lng ?? r?.longitude;

          const pt = coerceLatLonPair(latRaw, lonRaw) || coerceLatLonPair(lonRaw, latRaw);
          if (pt) coords.push(pt);
        }

        const elevs = records
          .map((r) => r?.enhanced_altitude ?? r?.altitude ?? r?.elevation)
          .filter((e) => typeof e === 'number' && !Number.isNaN(e));

        const ds = downsample(coords, 2500);
        const finalPts = ds.length > 1 ? ds : [];

        parsedCacheRef.current.set(selectedFileId, { points: finalPts, elev: elevs || [], meta });
        bumpCacheTick(); // ✅ refresca memos/tiles/totales

        setRouteFromParsed(finalPts);
        setElevFromParsed(elevs || []);
      } catch (e) {
        if (e?.name === 'AbortError') return;
        log('parsed-data error', e);
      } finally {
        if (mounted) setParsedLoading(false);
      }
    })();

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [uid, selectedFileId, bumpCacheTick]);

  const mapPoints = useMemo(() => {
    if (routeFromParsed.length > 1) return routeFromParsed;
    if (pointsFromWeeklySummary.length > 1) return pointsFromWeeklySummary;
    return [];
  }, [routeFromParsed, pointsFromWeeklySummary]);

  const bounds = useMemo(() => computeBoundsSafe(mapPoints), [mapPoints]);

  const isRunOrBike = selectedSportKey === 'running' || selectedSportKey === 'cycling';
  const canShowMap = isRunOrBike && !selectedPrimary?.isIndoor;

  const fallbackCenter = useMemo(() => {
    if (mapPoints.length) return mapPoints[0];

    const a = selectedPrimary || {};
    const lat1 = toNum(a?.start_position_lat ?? a?.session?.start_position_lat ?? a?.completed?.start_position_lat);
    const lon1 = toNum(a?.start_position_long ?? a?.session?.start_position_long ?? a?.completed?.start_position_long);
    if (isValidLatLon(lat1, lon1)) return [lat1, lon1];

    const dayPts = pointsFromWeeklySummary;
    if (dayPts?.length) return dayPts[0];

    return [-33.45, -70.66];
  }, [mapPoints, selectedPrimary, pointsFromWeeklySummary]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (!canShowMap) return;

    const t = setTimeout(() => {
      try {
        const m = mapRef.current;
        m.invalidateSize();
        if (bounds) m.fitBounds(bounds, { padding: [12, 12] });
        else if (fallbackCenter) m.setView(fallbackCenter, 13, { animate: false });
      } catch {}
    }, 140);

    return () => clearTimeout(t);
  }, [selectedDayKey, canShowMap, bounds, fallbackCenter, mapPoints.length]);

  const resolvedDurationMin = useMemo(() => {
    const m = getActDurationMin(selectedPrimary);
    const p = parsedMeta?.durationMin || 0;
    if (p > 0 && (m <= 0 || (m > 0 && m < 3 && p > 10))) return p;
    return m > 0 ? m : p;
  }, [selectedPrimary, parsedMeta]);

  const resolvedDistanceKm = useMemo(() => {
    const km = getActDistanceKm(selectedPrimary);
    const p = parsedMeta?.distanceKm || 0;
    if (p > 0 && (km <= 0 || (km > 0 && km < 0.2 && p > 1))) return p;
    return km > 0 ? km : p;
  }, [selectedPrimary, parsedMeta]);

  const resolvedTss = useMemo(() => {
    const v = getActTss(selectedPrimary);
    const p = parsedMeta?.tss || 0;
    return v > 0 ? v : p;
  }, [selectedPrimary, parsedMeta]);

  const durationSec = Math.round((resolvedDurationMin || 0) * 60);
  const paceSec =
    selectedSportKey === 'running' && resolvedDistanceKm > 0
      ? Math.round(durationSec / resolvedDistanceKm)
      : null;

  const rrssActivity = useMemo(() => {
    if (!selectedPrimary) return null;

    const type =
      selectedSportKey === 'cycling' ? 'Cycling'
      : selectedSportKey === 'running' ? 'Running'
      : selectedSportKey === 'swim' ? 'Swimming'
      : selectedSportKey === 'strength' ? 'Strength'
      : 'Running';

    return {
      type,
      name: selectedPrimary?.title || selectedPrimary?.name || 'Entrenamiento',
      completed: {
        distance: Math.round((resolvedDistanceKm || 0) * 1000),
        durationSec,
        pace: paceSec,
        tss: Math.round(resolvedTss || 0),
        elevGain: Math.round(getActElevGain(selectedPrimary) || 0),
        elevLoss: Math.round(getActElevLoss(selectedPrimary) || 0),
      },
      routePoints: mapPoints || [],
      fileId: selectedFileId || '',
    };
  }, [
    selectedPrimary,
    selectedSportKey,
    resolvedDistanceKm,
    resolvedTss,
    durationSec,
    paceSec,
    mapPoints,
    selectedFileId,
  ]);

  const handleGoDashboard = () => {
    if (typeof onGoDashboard === 'function') onGoDashboard();
    else window.location.assign('/dashboard');
  };

  const handlePrev = () => setWeekCursor((d) => addDays(d, -7));
  const handleNext = () => setWeekCursor((d) => addDays(d, +7));
  const handleToday = () => setWeekCursor(startOfWeekMonday(new Date()));

  const polyKey = useMemo(
    () => `${selectedDayKey}:${selectedFileId || 'nofile'}:${mapPoints.length}`,
    [selectedDayKey, selectedFileId, mapPoints.length]
  );

  const capturePreviewToBlob = useCallback(async () => {
    const node = sharePreviewRef.current;
    if (!node) throw new Error('No hay preview para capturar');

    await waitForImages(node, 6000);
    await nextPaint();
    await sleep(80);

    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      allowTaint: false,
      logging: false,
      imageTimeout: 15000,
      scrollX: 0,
      scrollY: -window.scrollY,
    });

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 1.0));
    if (!blob) throw new Error('No se pudo generar la imagen (blob null)');
    return blob;
  }, []);

  const selectedActOptions = useMemo(() => {
    void cacheRenderTick;

    const acts = selectedActs || [];
    return acts.map((a, idx) => {
      const id = safeId(getActId(a) || getFileIdFromActivity(a) || `idx-${idx}`);
      const sport = detectSportKeyFromActivity(a);

      const fid = getFileIdFromActivity(a);
      const cached = fid ? parsedCacheRef.current.get(fid) : null;

      const durMin = (cached?.meta?.durationMin > 0) ? cached.meta.durationMin : getActDurationMin(a);
      const distKm = (cached?.meta?.distanceKm > 0) ? cached.meta.distanceKm : getActDistanceKm(a);
      const tssVal = (cached?.meta?.tss > 0) ? cached.meta.tss : getActTss(a);

      const dur = formatDuration(durMin);
      const dist = Number(distKm || 0).toFixed(2);
      const tss = Math.round(tssVal || 0);
      const label = `${cap(sport)} • ${dur} • ${dist} km • ${tss} TSS`;

      return { id, label, sport, a };
    });
  }, [selectedActs, cacheRenderTick]);

  const selectedPrimaryKey = useMemo(() => {
    if (!selectedPrimary) return '';
    const idx = selectedActs.findIndex((a) => a === selectedPrimary);
    return safeId(getActId(selectedPrimary) || getFileIdFromActivity(selectedPrimary) || (idx >= 0 ? `idx-${idx}` : ''));
  }, [selectedPrimary, selectedActs]);

  const openShareModal = useCallback(async () => {
    if (!selectedActs.length) return;

    setShareOpen(true);
    setShareToId('');
    setShareContactsErr('');
    setShareNote(`Mira mi sesión del ${selectedLabel || ''} en Tzone 🗺️`.trim());

    setShareContactsLoading(true);
    try {
      const list = await fetchMatchContactsSmart(uid);
      setShareContacts(list);
    } catch (e) {
      setShareContacts([]);
      setShareContactsErr(e?.message || 'No se pudieron cargar contactos');
    } finally {
      setShareContactsLoading(false);
    }
  }, [selectedActs.length, selectedLabel, uid]);

  const closeShareModal = useCallback(() => {
    setShareOpen(false);
    setShareToId('');
    setShareContactsErr('');
  }, []);

  useEffect(() => {
    if (!shareOpen && !rrssOpen) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (rrssOpen) setRrssOpen(false);
        else closeShareModal();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [shareOpen, rrssOpen, closeShareModal]);

  const handleDownloadFromModal = useCallback(async () => {
    try {
      setSharing(true);
      const blob = await capturePreviewToBlob();
      const fileName = `tzone-activity-${selectedDayKey || 'day'}-${Date.now()}.png`;
      downloadBlob(blob, fileName);
    } catch (e) {
      console.error('[Share] download failed', e);
      alert('No se pudo generar/descargar la imagen.');
    } finally {
      setSharing(false);
    }
  }, [capturePreviewToBlob, selectedDayKey]);

  const handleShareToSocial = useCallback(async () => {
    try {
      setSharing(true);

      const blob = await capturePreviewToBlob();
      const fileName = `tzone-activity-${selectedDayKey || 'day'}-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      const canShareFiles =
        !!navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }));

      if (canShareFiles) {
        await navigator.share({
          title: 'Mi entrenamiento en Tzone',
          text: shareNote || 'Compartido desde Tzone',
          files: [file],
        });
        return;
      }

      downloadBlob(blob, fileName);
      alert(t('social.noSoportaCompartir'));
    } catch (e) {
      console.error('[Share] social share failed', e);
      alert(t('social.noPudoCompartir'));
    } finally {
      setSharing(false);
    }
  }, [capturePreviewToBlob, selectedDayKey, shareNote, t]);

  const handleSendToContact = useCallback(async () => {
    if (!uid) return alert('Falta userId para enviar.');
    if (!shareToId) return alert(t('social.seleccionaContacto'));

    try {
      setSharing(true);
      setShareContactsErr('');

      const blob = await capturePreviewToBlob();
      const fileName = `tzone-activity-${selectedDayKey || 'day'}-${Date.now()}.png`;

      const meta = {
        dayKey: selectedDayKey || null,
        activityId: getActId(selectedPrimary) || null,
        sport: selectedSportKey || null,
        fileId: selectedFileId || null,
      };

      const sent = await trySendShareToBackend({
        uid,
        toUserId: shareToId,
        text: shareNote || '',
        blob,
        fileName,
        meta,
      });

      if (sent.ok && (sent?.data?.persisted || sent?.data?.messageId)) {
        closeShareModal();
        alert('✅ Compartido con tu contacto (ver en “Mensajes”).');
        return;
      }

      if (sent.ok && sent?.data && sent?.data?.persisted === false) {
        setShareContactsErr('Se envió al backend, pero NO se guardó en Mensajes (persisted=false). Revisa el modelo SocialMessage.');
        downloadBlob(blob, fileName);
        alert('⚠️ Se generó la imagen, pero el backend no la guardó en Mensajes. Se descargó el PNG como respaldo.');
        return;
      }

      downloadBlob(blob, fileName);
      setShareContactsErr('No encontré un endpoint de mensajes para enviar. Descargué el PNG como respaldo.');
    } catch (e) {
      console.error('[Share] send failed', e);
      setShareContactsErr(e?.message || 'No se pudo enviar/compartir.');
    } finally {
      setSharing(false);
    }
  }, [
    uid,
    shareToId,
    capturePreviewToBlob,
    selectedDayKey,
    selectedPrimary,
    selectedSportKey,
    selectedFileId,
    shareNote,
    closeShareModal,
    t,
  ]);

  return (
    <div className={`tzone-weekly-card ${fullBleed ? 'is-fullbleed' : ''}`} ref={cardRef}>
      <div className="tzone-weekly-topbar">
        <div className="tzone-weekly-head">
          <div className="tzone-weekly-title">{title || t('social.weeklySpotlight.title')}</div>
          <div className="tzone-weekly-subtitle">{weekLabel}</div>
        </div>

        <div className="tzone-weekly-controls">
          <div className="tzone-weekly-nav" role="group" aria-label={t('social.weeklySpotlight.weekNavAriaLabel')}>
            <button type="button" className="tzone-weekly-navbtn" onClick={handlePrev}>{t('weekSelector.prev')}</button>
            <button type="button" className="tzone-weekly-navbtn" onClick={handleToday}>{t('weekSelector.today')}</button>
            <button type="button" className="tzone-weekly-navbtn" onClick={handleNext}>{t('weekSelector.next')}</button>
          </div>

          <button type="button" className="tzone-weekly-action" onClick={handleGoDashboard} title={t('social.weeklySpotlight.backToBoard')}>
            {t('social.weeklySpotlight.goToBoard')}
          </button>

          <button
            type="button"
            className="tzone-weekly-action"
            onClick={openShareModal}
            disabled={sharing || (selectedActs || []).length === 0}
            title={t('social.weeklySpotlight.shareWithContact')}
          >
            {t('social.weeklySpotlight.share')}
          </button>
        </div>
      </div>

      {loading && <div className="tzone-weekly-placeholder">{t('social.loadingResumen')}</div>}
      {!loading && err && <div className="tzone-weekly-error">{err}</div>}

      {!loading && !err && (
        <>
          <div className="tzone-weekly-metrics">
            <div className="tzone-weekly-metric">
              <div className="k">{t('weeklySummary.sessions')}</div>
              <div className="v">{totals.sessions || 0}</div>
            </div>

            <div className="tzone-weekly-metric">
              <div className="k">{t('weeklySummary.distance')}</div>
              <div className="v">{Number(totals.distanceKm || 0).toFixed(2)} km</div>
            </div>

            <div className="tzone-weekly-metric">
              <div className="k">{t('social.duracion')}</div>
              <div className="v">{formatDuration(totals.durationMin || 0)}</div>
            </div>

            <div className="tzone-weekly-metric">
              <div className="k">{t('weeklySummary.tss')}</div>
              <div className="v">{Math.round(totals.tss || 0)}</div>
            </div>
          </div>

          <div className="tzone-weekly-body">
            <div className="tzone-weekly-main">
              <div className="tzone-weekly-sectionTitle">{t('weekSelector.week')}</div>

              <div className="tzone-weekly-calwrap" aria-label="Calendario semanal">
                <div className="tzone-weekly-days-grid">
                  {dayTiles.map((d) => {
                    const isSelected = d.key === selectedDayKey;
                    const sport = d.primarySport || 'dayoff';
                    const icon = sportIconForKey(sport);

                    return (
                      <button
                        key={d.key}
                        type="button"
                        className={['tzone-weekly-day', isSelected ? 'is-selected' : '', `sport-${sport}`].join(' ')}
                        onClick={() => {
                          setSelectedDayKey(d.key);
                          setSelectedActId('');
                        }}
                        title={t('social.verDetalleDia')}
                      >
                        <div className="tzone-weekly-day-hdr">
                          <div className="tzone-weekly-day-date">
                            <div className="dow">{cap(shortDow(d.date))}</div>
                            <div className="dm">{shortDM(d.date)}</div>
                          </div>
                          <img className="tzone-weekly-day-ico" src={icon} alt={sport} />
                        </div>

                        {d.sessions > 0 ? (
                          <>
                            <div className="tzone-weekly-day-big">{formatCompactHM(d.durationMin)}</div>

                            <div className="tzone-weekly-day-sub">
                              <span>{Number(d.distanceKm || 0).toFixed(1)} km</span>
                              <span className="dot">•</span>
                              <span>{Math.round(d.tss || 0)} TSS</span>
                            </div>

                            {d.titleText ? (
                              <div className="tzone-weekly-day-note" title={d.titleText}>
                                {d.titleText}
                              </div>
                            ) : (
                              <div className="tzone-weekly-day-note mutedNote">{t('weeklySummary.sessions')}: {d.sessions}</div>
                            )}
                          </>
                        ) : (
                          <div className="tzone-weekly-day-rest">{t('activityLabels.rest')}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="tzone-weekly-summarybar">
                <div className="tzone-weekly-summaryitem">
                  <div className="lbl">{t('weeklySummary.duration')}</div>
                  <div className="val">{formatCompactHM(totals.durationMin)}</div>
                </div>

                <div className="tzone-weekly-summaryitem">
                  <div className="lbl">{t('weeklySummary.distance')}</div>
                  <div className="val">{Number(totals.distanceKm || 0).toFixed(1)} km</div>
                </div>

                <div className="tzone-weekly-summaryitem">
                  <div className="lbl">{t('weeklySummary.tss')}</div>
                  <div className="val">{Math.round(totals.tss || 0)}</div>
                </div>

                <div className="tzone-weekly-summaryitem">
                  <div className="lbl">{t('weeklySummary.elevation')}</div>
                  <div className="val">
                    +{Math.round(totals.elevGain || 0)} / -{Math.round(totals.elevLoss || 0)}
                  </div>
                </div>
              </div>
            </div>

            <aside className="tzone-weekly-side">
              <div className="tzone-weekly-sideHead">
                <div className="tzone-weekly-sideTitle">{t('activitySummary.title')}: {selectedLabel || '—'}</div>
                <div className="tzone-weekly-sideSub">{t('activitySummary.routeMap')}</div>
              </div>

              {selectedActs.length === 0 ? (
                <div className="tzone-weekly-placeholder small">{t('social.diaDescanso')}</div>
              ) : (
                <div className="tzone-weekly-detailCard">
                  <div className="tzone-weekly-detailLeft">
                    <img
                      className="tzone-weekly-detailIcon"
                      src={sportIconForKey(selectedSportKey)}
                      alt={selectedSportKey}
                    />

                    <div className="tzone-weekly-detailText">
                      <div className="tzone-weekly-detailName">
                        {cap(selectedSportKey === 'swim' ? 'Swim' : selectedSportKey)}
                      </div>

                      {selectedActOptions.length > 1 && (
                        <div className="tzone-weekly-actpicker">
                          <div className="lbl">{t('social.sesionLabel')}</div>
                          <select
                            value={safeId(selectedActId) || selectedPrimaryKey}
                            onChange={(e) => setSelectedActId(e.target.value)}
                          >
                            {selectedActOptions.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="tzone-weekly-detailMeta">
                        <span><b>{t('weeklySummary.duration')}</b> {formatDuration(resolvedDurationMin)}</span>
                        {selectedSportKey !== 'strength' && (
                          <span><b>{t('weeklySummary.distance')}</b> {Number(resolvedDistanceKm).toFixed(2)} km</span>
                        )}
                        <span><b>{t('weeklySummary.tss')}</b> {Math.round(resolvedTss)}</span>
                      </div>

                      {selectedSportKey === 'running' && resolvedDistanceKm > 0 && (
                        <div className="tzone-weekly-detailMeta">
                          <span><b>{t('summary.avgPace')}</b> {formatPaceFromSec(paceSec)} /km</span>
                        </div>
                      )}

                      <div className="tzone-weekly-detailMeta2">
                        <span>{t('social.elvMas')} {Math.round(getActElevGain(selectedPrimary))} m</span>
                        <span><b>ELV -</b> {Math.round(getActElevLoss(selectedPrimary))} m</span>
                        <span><b>{t('weeklySummary.sessions')}</b> {selectedActs.length}</span>
                      </div>

                      {isRunOrBike && (
                        <div className="muted" style={{ marginTop: 6 }}>
                          {parsedLoading
                            ? 'Cargando ruta…'
                            : `fileId=${selectedFileId || '—'} | pts=${mapPoints.length} | elevRec=${elevFromParsed.length}`}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="tzone-weekly-detailMap">
                    {canShowMap ? (
                      <div className="tzone-weekly-mapThumb">
                        <MapContainer
                          whenCreated={(m) => { mapRef.current = m; }}
                          center={fallbackCenter}
                          zoom={13}
                          scrollWheelZoom
                          zoomControl
                          dragging
                          doubleClickZoom
                          touchZoom
                          preferCanvas
                          style={{ width: '100%', height: 180 }}
                        >
                          <TileLayer
                            attribution={TILE_ATTR}
                            url={TILE_URL}
                            subdomains={TILE_SUBDOMAINS}
                            crossOrigin="anonymous"
                          />

                          {mapPoints.length > 1 && (
                            <>
                              <Polyline key={polyKey} positions={mapPoints} />
                              <CircleMarker center={mapPoints[0]} radius={5} pathOptions={{}} />
                              <CircleMarker center={mapPoints[mapPoints.length - 1]} radius={5} pathOptions={{}} />
                            </>
                          )}
                        </MapContainer>
                      </div>
                    ) : (
                      <div className="tzone-weekly-mapPlaceholder" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, height: 180, background: 'linear-gradient(135deg,#f1f5f9,#e2e8f0)' }}>
                        <div style={{ position: 'relative' }}>
                          <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#193255', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }}>
                            <span style={{ color: '#fbc775', fontWeight: 800, fontSize: 14 }}>TZ</span>
                          </div>
                          <div style={{ width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '14px solid #193255', margin: '0 auto' }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>Sin datos GPS</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="tzone-weekly-footer">
                <button
                  type="button"
                  className="tzone-weekly-primary"
                  onClick={openShareModal}
                  disabled={sharing || selectedActs.length === 0}
                >
                  {sharing ? t('social.weeklySpotlight.generating') : t('social.weeklySpotlight.share')}
                </button>
                <div className="muted">{t('social.comparteConContactos')}</div>
              </div>
            </aside>
          </div>

          {/* ✅ MODAL COMPARTIR */}
          {shareOpen && (
            <div
              className="tzone-share-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label="Compartir"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeShareModal();
              }}
            >
              <div className="tzone-share-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tzone-share-header">
                  <div>
                    <div className="tzone-share-title">{t('social.compartirSesionTitle')}</div>
                    <div className="tzone-share-sub">{t('social.compartirSesionSteps')}</div>
                  </div>
                  <button type="button" className="tzone-share-close" onClick={closeShareModal} aria-label="Cerrar">
                    ✕
                  </button>
                </div>

                <div className="tzone-share-grid">
                  <div className="tzone-share-panel">
                    <div className="tzone-share-section">
                      <div className="tzone-share-label">{t('social.sesionDelDia')}</div>
                      {selectedActOptions.length <= 1 ? (
                        <div className="tzone-share-chip">{selectedActOptions[0]?.label || 'Sesión'}</div>
                      ) : (
                        <select
                          className="tzone-share-select"
                          value={safeId(selectedActId) || selectedPrimaryKey}
                          onChange={(e) => setSelectedActId(e.target.value)}
                        >
                          {selectedActOptions.map((o) => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="tzone-share-section">
                      <div className="tzone-share-label">{t('social.contact')}</div>

                      {shareContactsLoading ? (
                        <div className="tzone-share-muted">{t('social.cargandoContactos')}</div>
                      ) : shareContacts.length === 0 ? (
                        <div className="tzone-share-muted">
                          No hay contactos (o no pude cargar).<br />
                          <span className="tzone-share-mutedSmall">Si “Mis contactos” funciona, este modal necesita el mismo endpoint.</span>
                        </div>
                      ) : (
                        <div className="tzone-share-contacts">
                          {shareContacts.map((c) => {
                            const initial = String(c?.name || 'C').trim().slice(0, 1).toUpperCase();
                            const scope = c?.role === 'trainer' ? 'trainer' : 'athlete';
                            const resolved = resolvePhotoUrl(c?.avatar, { scope });
                            const fallback = scope === 'trainer' ? DEFAULT_TRAINER_AVATAR : DEFAULT_ATHLETE_AVATAR;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                className={['tzone-share-contact', shareToId === c.id ? 'is-selected' : ''].join(' ')}
                                onClick={() => setShareToId(c.id)}
                                title={c.name}
                              >
                                {resolved ? (
                                  <img
                                    className="tzone-share-avatar"
                                    src={resolved}
                                    alt={c.name}
                                    onError={(e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = fallback;
                                    }}
                                  />
                                ) : (
                                  <div className="tzone-share-avatar placeholder">{initial}</div>
                                )}
                                <div className="tzone-share-contactName">{c.name}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {shareContactsErr ? <div className="tzone-share-error">{shareContactsErr}</div> : null}
                    </div>

                    <div className="tzone-share-section">
                      <div className="tzone-share-label">{t('social.messagesPage.writeMessage').replace('…','')}</div>
                      <textarea
                        className="tzone-share-textarea"
                        value={shareNote}
                        onChange={(e) => setShareNote(e.target.value)}
                        rows={3}
                        placeholder={t('social.escribeMensaje')}
                      />
                    </div>

                    <div className="tzone-share-actions">
                      <button type="button" className="tzone-share-btn ghost" onClick={closeShareModal}>
                        Cancelar
                      </button>

                      <button
                        type="button"
                        className="tzone-share-btn"
                        onClick={handleDownloadFromModal}
                        disabled={sharing}
                        title={t('social.generaPng')}
                      >
                        {sharing ? 'Generando…' : 'Descargar PNG'}
                      </button>

                      <button
                        type="button"
                        className="tzone-share-btn"
                        onClick={() => setRrssOpen(true)}
                        disabled={sharing || !rrssActivity}
                        title="Abrir card 1080x1920"
                      >
                        {t('social.publishOnSocialMedia')}
                      </button>

                      <button
                        type="button"
                        className="tzone-share-btn"
                        onClick={handleShareToSocial}
                        disabled={sharing}
                        title={t('social.sharePng')}
                      >
                        Share (nativo)
                      </button>

                      <button
                        type="button"
                        className="tzone-share-btn primary"
                        onClick={handleSendToContact}
                        disabled={sharing || !shareToId}
                        title="Enviar a contacto"
                      >
                        {sharing ? 'Enviando…' : 'Enviar'}
                      </button>
                    </div>
                  </div>

                  {/* Right: preview capturable */}
                  <div className="tzone-share-previewWrap">
                    <div className="tzone-share-previewTitle">{t('social.vistaPrevia')}</div>

                    <div className="tzone-share-preview" ref={sharePreviewRef}>
                      <div className="tzone-share-cardHead">
                        <img
                          src={tzoneLogo}
                          alt="Tzone"
                          className="tzone-share-logo"
                        />
                        <div className="tzone-share-date">{selectedLabel || ''}</div>
                      </div>

                      <div className="tzone-share-cardBody">
                        <div className="tzone-share-row">
                          <img className="tzone-share-sportIcon" src={sportIconForKey(selectedSportKey)} alt={selectedSportKey} />
                          <div className="tzone-share-rowText">
                            <div className="tzone-share-sport">{cap(selectedSportKey)}</div>
                            <div className="tzone-share-stats">
                              <span><b>Dur</b> {formatDuration(resolvedDurationMin)}</span>
                              {selectedSportKey !== 'strength' && (
                                <span><b>{t('weeklySummary.distance')}</b> {Number(resolvedDistanceKm).toFixed(2)} km</span>
                              )}
                              <span><b>{t('weeklySummary.tss')}</b> {Math.round(resolvedTss)}</span>
                              {selectedSportKey === 'running' && resolvedDistanceKm > 0 && (
                                <span><b>{t('summary.avgPace')}</b> {formatPaceFromSec(paceSec)} /km</span>
                              )}
                            </div>
                            <div className="tzone-share-stats2">
                              <span><b>{t('weeklySummary.elevation')}</b> +{Math.round(getActElevGain(selectedPrimary))} / -{Math.round(getActElevLoss(selectedPrimary))}</span>
                              <span className="muted">{t('social.tzoneDomain')}</span>
                            </div>
                          </div>
                        </div>

                        <div className="tzone-share-mapBox">
                          {canShowMap && mapPoints.length > 1 ? (
                            <RouteSvgPreview points={mapPoints} />
                          ) : (
                            <div className="tzone-share-mapEmpty">{t('summary.notAvailable')}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="tzone-share-previewHint">
                      Nota: para evitar problemas CORS, el PNG usa “ruta SVG” (estable). El mapa interactivo sigue en la UI.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ✅ MODAL RRSS 1080x1920 */}
          {rrssOpen && rrssActivity && (
            <div
              className="tzone-share-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label="Publicar en RRSS"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setRrssOpen(false);
              }}
            >
              <div className="tzone-share-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tzone-share-header">
                  <div>
                    <div className="tzone-share-title">{t('social.publishOnSocialMedia')}</div>
                    <div className="tzone-share-sub">{t('social.generaCard')}</div>
                  </div>
                  <button type="button" className="tzone-share-close" onClick={() => setRrssOpen(false)}>✕</button>
                </div>

                <ShareTrainingCardPanel
                  activity={rrssActivity}
                  photoUrl={null}
                  mapImageUrl={null}
                  onClose={() => setRrssOpen(false)}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
